/**
 * One cache for the "derive something from a store map, once per store tick"
 * pattern — the shape both clients had hand-rolled once per selector.
 *
 * Why it exists: Zustand runs EVERY subscriber's selector on EVERY store tick,
 * and the sync store replaces its maps wholesale on any write. A selector that
 * walks a whole map therefore runs once per subscribed component per tick — and
 * these selectors run in every visible deck / folder / group tile. Keying the
 * result on the SOURCE MAP's identity means the first tile to ask does the walk
 * and every other tile in that tick gets the same object back (which is also
 * what lets `useShallow` suppress their re-renders).
 *
 * `WeakMap`-keyed, not a single slot: a single slot is correct but thrashes as
 * soon as two callers pass different `variant`s (three group tiles = three
 * walks per tick, each evicting the last). It also lets a stale store map be
 * collected instead of pinned.
 *
 * ONLY for pure derivations. `derive` must depend on nothing but its arguments,
 * or a cache hit will hand back an answer to a different question.
 */
export function createDerivation<S extends object, R>(
  derive: (source: S, variant: string) => R,
): (source: S, variant?: string) => R {
  const cache = new WeakMap<S, Map<string, R>>();
  return (source: S, variant = '') => {
    let byVariant = cache.get(source);
    if (!byVariant) {
      byVariant = new Map<string, R>();
      cache.set(source, byVariant);
    }
    const cached = byVariant.get(variant);
    // `has` rather than a truthy check: a derivation may legitimately return
    // undefined/null and must not be recomputed every tick because of it.
    if (cached !== undefined || byVariant.has(variant)) return cached as R;
    const result = derive(source, variant);
    byVariant.set(variant, result);
    return result;
  };
}

/**
 * The entries of `all` for exactly `ids`, in one small object — the standard way
 * to narrow a wholesale-replaced store map to the rows one component displays.
 * Pair it with `useShallow`: the result stays shallow-equal while those entries
 * keep their identities, whatever happens to the rest of the map.
 *
 * Selects by MAP KEY. When the ids you hold are a row FIELD (`props.id`) rather
 * than the key, narrow with a predicate over the rows instead — see
 * `linkedDeckRows`.
 *
 * OWN properties only, and the result has a NULL PROTOTYPE. An id here can be a
 * user-named string (a DeckPath), so `id in all` would report `constructor` /
 * `toString` as present on any map and copy an inherited function in as if it
 * were a store row, and a plain `out` would let `__proto__` write through
 * instead of becoming a key.
 */
export function pickRecord<T>(
  all: Record<string, T> | undefined | null,
  ids: Iterable<string>,
): Record<string, T> {
  const out: Record<string, T> = Object.create(null);
  if (!all) return out;
  for (const id of ids) {
    if (Object.prototype.hasOwnProperty.call(all, id)) out[id] = all[id];
  }
  return out;
}

/**
 * Two-source variant of {@link createDerivation}, for a derivation of TWO
 * inputs — a store map and a list, or two store maps — that must be recomputed
 * when EITHER is replaced, and only then.
 *
 * Keyed on BOTH identities through nested `WeakMap`s. Do not fake a string
 * `variant` out of the second input (`folders.length`, a joined id list): a
 * length is not an identity — a rename that keeps the count would have served
 * the previous answer — and a joined key is an O(n) string build per call, per
 * subscriber, per tick, which is the cost this cache exists to remove. The
 * same purity rule as the one-source form: `derive` depends on nothing but its
 * two arguments.
 */
export function createDerivation2<A extends object, B extends object, R>(
  derive: (a: A, b: B) => R,
): (a: A, b: B) => R {
  const cache = new WeakMap<A, WeakMap<B, R>>();
  return (a, b) => {
    let byB = cache.get(a);
    if (!byB) {
      byB = new WeakMap<B, R>();
      cache.set(a, byB);
    }
    // `has`, not a truthy check — see `createDerivation`.
    if (byB.has(b)) return byB.get(b) as R;
    const result = derive(a, b);
    byB.set(b, result);
    return result;
  };
}
